import 'uno.css'
import React, { lazy, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './utils/enrollmentHistoryGuard';

// Loading fallback for lazy-loaded components
const LazyLoadFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

// === Static imports (needed immediately: auth, layout, providers, utilities) ===
import SkipNavigation from './components/ui/SkipNavigation';
import Login from './Login';
import AdminShell from './admin/AdminShell';
import ProtectedRoute from './components/ProtectedRoute';
import FormClientBoundary from './components/forms/FormClientBoundary';
import PermissionProtectedRoute from './components/PermissionProtectedRoute';
import WeCareHrRoute from './components/WeCareHrRoute';
import OrgFeatureProtectedRoute from './components/OrgFeatureProtectedRoute';
import ExternalIntegrationProtectedRoute from './components/ExternalIntegrationProtectedRoute';
import AiSupervisoryReviewProtectedRoute from './components/AiSupervisoryReviewProtectedRoute';
import ClinicalApprovalProtectedRoute from './components/ClinicalApprovalProtectedRoute';
import BundleProtectedRoute from './components/BundleProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import SessionMonitor from './components/SessionMonitor';
import DraftRecoveryPrompt from './components/clinical/DraftRecoveryPrompt';
import InactivityMonitor from './components/InactivityMonitor';
import ActivityBasedTokenRefresh from './components/ActivityBasedTokenRefresh';
import SessionDebugWidget from './components/SessionDebugWidget';
import { AuthProvider } from "./AuthContext.jsx";
import { AccessControlProvider } from "./hooks/useAccessControl.jsx";
import { FeatureVisibilityProvider } from './contexts/FeatureVisibilityContext';
import { IntegratedSidebarProvider } from './contexts/IntegratedSidebarContext.jsx';
import './utils/tokenCleanup';
import './styles/animations.css';
import { UserManagementProvider } from "./context/UserManagementContext";
import AlertsProvider from "./context/AlertsContext.jsx";
import NotificationsProvider from "./context/NotificationsContext.jsx";

const CLINICAL_BUNDLES = ['behavioral-health', 'mh-co-responder', '245d-waiver-services'];
// The ADS fillable-form windows carry templates (IAPP, ICP, client rights,
// incident report, ...) that are 245D/DHS constructs too, so a 245D-only
// tenant reaches them from its 245D program modules. Mirrors the backend gate
// on /api/v1/adult-day-services/forms.
// Residential Services files its 144G forms through the same windows.
const ADS_FORM_BUNDLES = ['adult-day-services', '245d-waiver-services', 'residential-services'];
// Clinical windows: the bundle AND the permission their load/save APIs accept
// (clinical_access; view/complete_assessments for read-only assessment views),
// so a window cannot be opened by URL without clinical permission.
const CLINICAL_WINDOW_PERMISSIONS = ['clinical_access', 'view_assessments', 'complete_assessments'];
const ClinicalBundleGate = ({ children }) => (
  <BundleProtectedRoute requiredBundle={CLINICAL_BUNDLES}>
    <PermissionProtectedRoute requiredAnyPermissions={CLINICAL_WINDOW_PERMISSIONS}>{children}</PermissionProtectedRoute>
  </BundleProtectedRoute>
);
const RepositoryRoiRouteGate = ({ children }) => {
  const isCoResponderRepository = new URLSearchParams(window.location.search)
    .get('repositoryContext') === 'co-responder';
  if (isCoResponderRepository) {
    return <BundleProtectedRoute requiredBundle="mh-co-responder">{children}</BundleProtectedRoute>;
  }
  return (
    <ClinicalBundleGate>
      <OrgFeatureProtectedRoute requiredFeature="wecare_template_forms">{children}</OrgFeatureProtectedRoute>
    </ClinicalBundleGate>
  );
};
import { ThemeProvider, useTheme } from "./hooks/useTheme.jsx";
import { usePageTitle } from "./hooks/usePageTitle";
import RoleRedirect from './RoleRedirect';
import RenamedRoute from './components/RenamedRoute';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LiveRegionProvider } from './components/ui/LiveRegion';

// Patient Portal - static imports for auth/layout only
import { PatientAuthProvider, PatientProtectedRoute } from './patient-portal';

// === Lazy-loaded page components (code-split into separate chunks) ===

// Admin pages
const AdminDashboard = lazy(() => import('./admin/AdminDashboard'));
const EnhancedScheduler = lazy(() => import('./admin/EnhancedScheduler'));
const AlertsPage = lazy(() => import('./admin/AlertsPage'));
const VisitCalendar = lazy(() => import('./admin/VisitCalendar'));
const VisitLogs = lazy(() => import('./admin/VisitLogs'));
const CaregiverSearch = lazy(() => import('./admin/CaregiverSearch'));
const Patients = lazy(() => import('./admin/Patients'));
const WaitlistManagement = lazy(() => import('./admin/WaitlistManagement'));
const ReferralManagement = lazy(() => import('./admin/ReferralManagement'));
const IntakePipeline = lazy(() => import('./admin/IntakePipeline'));
const CaregiverRequests = lazy(() => import('./admin/CaregiverRequests'));
const DAScheduling = lazy(() => import('./admin/DAScheduling'));
const ContactsList = lazy(() => import('./admin/ContactsList'));
const Resources = lazy(() => import('./admin/Resources'));
const TrainingLibrary = lazy(() => import('./pages/training/TrainingLibrary'));
const TrainingPlayer = lazy(() => import('./pages/training/TrainingPlayer'));
const Settings = lazy(() => import('./admin/Settings'));
const NotificationSettings = lazy(() => import('./admin/NotificationSettings'));
const Profile = lazy(() => import('./admin/Profile'));
const Reports = lazy(() => import('./admin/Reports'));
const ModernAnalytics = lazy(() => import('./admin/ModernAnalytics'));
const ServiceLinesOptimized = lazy(() => import('./admin/ServiceLinesOptimized'));
const Communication = lazy(() => import('./admin/Communication'));
const IssueReports = lazy(() => import('./admin/IssueReports'));
const Announcements = lazy(() => import('./admin/Announcements'));
const NarrativeSimilarityFindings = lazy(() => import('./admin/NarrativeSimilarityFindings'));
const RecordsRequestsPage = lazy(() => import('./admin/recordsRequests/RecordsRequestsPage'));
const RecordsRequestDetail = lazy(() => import('./admin/recordsRequests/RecordsRequestDetail'));
const SmsConsent = lazy(() => import('./admin/SmsConsent'));
const TextAlerts = lazy(() => import('./admin/TextAlerts'));
const UsageAnalytics = lazy(() => import('./admin/UsageAnalytics'));
const FrictionAnalytics = lazy(() => import('./admin/FrictionAnalytics'));
const AiUsage = lazy(() => import('./admin/AiUsage'));
const OnCallSettings = lazy(() => import('./admin/OnCallSettings'));
const FaxCenter = lazy(() => import('./admin/FaxCenter'));
const VisitsToday = lazy(() => import('./admin/VisitsToday'));
const ModernAuditLogs = lazy(() => import('./admin/ModernAuditLogs'));
const VisitManagement = lazy(() => import('./admin/VisitManagement'));
const ServiceCodesManagement = lazy(() => import('./admin/ServiceCodesManagement'));
const VisitNotesManagement = lazy(() => import('./admin/VisitNotesManagement'));
const QualityReviewsManagement = lazy(() => import('./admin/QualityReviewsManagement'));
const Billing = lazy(() => import('./admin/Billing'));
const AccountingLedger = lazy(() => import('./admin/AccountingLedger'));
const VisitHistory = lazy(() => import('./admin/VisitHistory'));
const VisitDetailPage = lazy(() => import('./admin/VisitDetailPage'));
const BillingReports = lazy(() => import('./admin/BillingReports'));
const BillingClaims = lazy(() => import('./admin/BillingClaims'));
const ClaimStatusPage = lazy(() => import('./admin/ClaimStatusPage'));
const GrantInvoices = lazy(() => import('./admin/GrantInvoices'));
const GrantReport = lazy(() => import('./admin/GrantReport'));
const DenialManagementPage = lazy(() => import('./admin/DenialManagementPage'));
const ERAReconciliation = lazy(() => import('./admin/ERAReconciliation'));
const ClientStatement = lazy(() => import('./admin/ClientStatement'));
const DataReadiness = lazy(() => import('./admin/DataReadiness'));
const SystemHealth = lazy(() => import('./admin/SystemHealth'));
const PersonalCareProgram = lazy(() => import('./admin/PersonalCareProgram'));
const AdultDayServicesProgramWrapper = lazy(() => import('./admin/AdultDayServicesProgramWrapper'));
const BehavioralHealthProgram = lazy(() => import('./admin/BehavioralHealthProgram'));
const HomeHealthProgram = lazy(() => import('./admin/HomeHealthProgram'));
const Program245DBasic = lazy(() => import('./admin/Program245DBasic'));
const Program245DReadinessDashboard = lazy(() => import('./components/program245d/Program245DReadinessDashboard'));
const SupportReportsWorkspace = lazy(() => import('./components/program245d/SupportReportsWorkspace'));
const Program245DIntensive = lazy(() => import('./admin/Program245DIntensive'));
const AdminNotFound = lazy(() => import('./admin/AdminNotFound'));
const ResidentialServicesProgram = lazy(() => import('./admin/Residential'));
const CaseManagementProgram = lazy(() => import('./admin/CaseManagement'));
const HousingSupportProgram = lazy(() => import('./admin/HousingSupport'));
const TimeTrackingPage = lazy(() => import('./admin/TimeTrackingPage'));
const ClinicalPage = lazy(() => import('./admin/ClinicalPage'));
const AIAnalytics = lazy(() => import('./admin/AIAnalytics'));
const ClinicalImports = lazy(() => import('./admin/ClinicalImports'));
const DataImportPage = lazy(() => import('./admin/dataImport/DataImportPage'));
const KnowledgeBase = lazy(() => import('./admin/KnowledgeBase'));
const ClinicalTreatmentPlans = lazy(() => import('./admin/ClinicalTreatmentPlans'));
const ClinicalNotes = lazy(() => import('./admin/ClinicalNotes'));
const ClinicalAssessments = lazy(() => import('./admin/ClinicalAssessments'));
const PayerManagement = lazy(() => import('./admin/PayerManagement'));
const Programs = lazy(() => import('./admin/Programs'));
const ProgramView = lazy(() => import('./admin/ProgramView'));
// DocumentManagement removed — not wired into sidebar navigation
const SystemUsers = lazy(() => import('./admin/SystemUsers'));
const ProviderTypeManagement = lazy(() => import('./admin/ProviderTypeManagement'));
const IntegratedPermissions = lazy(() => import('./admin/IntegratedPermissions'));
const FeatureVisibilityAdmin = lazy(() => import('./admin/FeatureVisibilityAdmin'));
const OfficeManagement = lazy(() => import('./admin/OfficeManagement'));
const MissedVisitManagement = lazy(() => import('./admin/MissedVisitManagement'));
const HHAStatusDashboard = lazy(() => import('./admin/HHAStatusDashboard'));
const AppointmentsPage = lazy(() => import('./admin/AppointmentsPage'));
const ClinicalChartReport = lazy(() => import('./admin/ClinicalChartReport'));
const SimplifiedTimezoneManager = lazy(() => import('./admin/SimplifiedTimezoneManager'));
const DocumentExpirationDashboard = lazy(() => import('./admin/DocumentExpirationDashboard'));
const StaffCredentialManagement = lazy(() => import('./admin/StaffCredentialManagement'));
const AuthorizationGuardrails = lazy(() => import('./admin/AuthorizationGuardrails'));
const ComplianceRuleEngine = lazy(() => import('./admin/ComplianceRuleEngine'));
const BillingComplianceDashboard = lazy(() => import('./admin/BillingComplianceDashboard'));
const ComplianceOverviewDashboard = lazy(() => import('./admin/ComplianceOverviewDashboard'));
const PCASupervisionTracking = lazy(() => import('./admin/PCASupervisionTracking'));
const WorkerTrainingRedirect = lazy(() => import('./components/homehealth/WorkerTrainingRedirect'));
const EligibilityCheck = lazy(() => import('./admin/EligibilityCheckRedesigned'));
const BillingBatches = lazy(() => import('./admin/BillingBatches'));
const PeopleHr = lazy(() => import('./admin/PeopleHr'));
const HrApplicants = lazy(() => import('./admin/HrApplicants'));
const MyGoals = lazy(() => import('./admin/peopleHr/MyGoals'));
const HrNewHireStart = lazy(() => import('./admin/peopleHr/newHire/HrNewHireStart'));
const HrNewHireReview = lazy(() => import('./admin/peopleHr/newHire/HrNewHireReview'));
const CeoWorkspace = lazy(() => import('./admin/CeoWorkspace'));
const ExclusionChecks = lazy(() => import('./admin/ExclusionChecks'));
const BillingAgentDashboard = lazy(() => import('./admin/BillingAgentDashboard'));
const CoResponderCaseloadPage = lazy(() => import('./admin/CoResponderCaseloadPage'));
const CoResponderDocumentsPage = lazy(() => import('./admin/CoResponderDocumentsPage'));
const CommunityProvidersPage = lazy(() => import('./admin/CommunityProvidersPage'));
const EncampmentsPage = lazy(() => import('./admin/EncampmentsPage'));
const ClientStatSurveys = lazy(() => import('./admin/ClientStatSurveys'));

// Platform admin pages
const PlatformLiveSupport = lazy(() => import('./platform/pages/PlatformLiveSupport'));
const PlatformDashboard = lazy(() => import('./platform/PlatformDashboard'));
const OrganizationsListView = lazy(() => import('./platform/OrganizationsListView'));
const PlatformDemoScheduling = lazy(() => import('./platform/PlatformDemoScheduling'));
const PlatformCrmPage = lazy(() => import('./platform/pages/PlatformCrmPage'));
const PlatformAdminsPage = lazy(() => import('./platform/pages/PlatformAdminsPage'));
const PlatformBrandingPage = lazy(() => import('./platform/pages/PlatformBrandingPage'));
const PlatformBillingPage = lazy(() => import('./platform/pages/PlatformBillingPage'));
const PaymentResultPage = lazy(() => import('./platform/pages/PaymentResultPage'));
const InvoicePayPage = lazy(() => import('./platform/pages/InvoicePayPage'));
const AppUpdatesPage = lazy(() => import('./platform/pages/AppUpdatesPage'));
const SystemHealthPage = lazy(() => import('./platform/pages/SystemHealthPage'));

// Clinical page components (pages/clinical)
const SupervisorApprovals = lazy(() => import('./pages/clinical/SupervisorApprovals'));
const ClientSignatureWaivers = lazy(() => import('./pages/clinical/ClientSignatureWaivers'));
const AIComplianceScanResults = lazy(() => import('./pages/clinical/AIComplianceScanResults'));
const PolicyAssistantPage = lazy(() => import('./pages/PolicyAssistantPage'));

// Document viewer pages
const ProgressNoteViewer = lazy(() => import('./admin/ProgressNoteViewer'));
const PcaDocumentReader = lazy(() => import('./admin/HomeHealthProgram/components/documents/PcaDocumentReader'));
const HealthReviewViewer = lazy(() => import('./admin/HealthReviewViewer'));
const ADSFormViewer = lazy(() => import('./admin/ADSFormViewer'));
const DiagnosticAssessmentViewer = lazy(() => import('./admin/DiagnosticAssessmentViewer'));

// Billing / invoice components
const InvoiceGeneratorEnhanced = lazy(() => import('./components/InvoiceGeneratorEnhanced'));
const PayerDetailWindow = lazy(() => import('./components/PayerDetailWindow'));
const OfficeAllyIntegration = lazy(() => import('./components/OfficeAllyIntegration'));
const SignatureManager = lazy(() => import('./components/settings/SignatureManager'));

// Clinical document windows (opened via window.open)
const MHProgressNoteWindow = lazy(() => import('./components/MHProgressNoteWindow'));
const ARMHSProgressNoteWindow = lazy(() => import('./components/ARMHSProgressNoteWindow'));
const ADSProgressNoteWindow = lazy(() => import('./components/ADSProgressNoteWindow'));
const DiagnosticAssessmentWindow = lazy(() => import('./components/DiagnosticAssessmentWindow'));
const MHDiagnosticAssessmentWindow = lazy(() => import('./components/MHDiagnosticAssessmentWindow'));
const StandardDiagnosticAssessmentWindow = lazy(() => import('./components/StandardDiagnosticAssessmentWindow'));
const YZCareTapDiagnosticAssessmentWindow = lazy(() => import('./components/YZCareTapDiagnosticAssessmentWindow'));
const PHQ9AssessmentWindow = lazy(() => import('./components/PHQ9AssessmentWindow'));
const GAD7AssessmentWindow = lazy(() => import('./components/GAD7AssessmentWindow'));
const PCL5AssessmentWindow = lazy(() => import('./components/PCL5AssessmentWindow'));
const FunctionalAssessmentWindow = lazy(() => import('./components/FunctionalAssessmentWindow'));
const YZCareTapFunctionalAssessmentWindow = lazy(() => import('./components/YZCareTapFunctionalAssessmentWindow'));
const YZFunctionalAssessmentWindow = lazy(() => import('./components/YZFunctionalAssessmentWindow'));
const DiagnosticAssessmentAddendumWindow = lazy(() => import('./components/DiagnosticAssessmentAddendumWindow'));
const IAPPAddendumWindow = lazy(() => import('./components/IAPPAddendumWindow'));
const UploadedDiagnosticAssessmentViewer = lazy(() => import('./admin/UploadedDiagnosticAssessmentViewer'));
const TreatmentPlanWindow = lazy(() => import('./components/TreatmentPlanWindow'));
const YZCareTapTreatmentPlanWindow = lazy(() => import('./components/YZCareTapTreatmentPlanWindow'));
const YZCareTapProgressNoteWindow = lazy(() => import('./components/YZCareTapProgressNoteWindow'));
const MonthlyHealthReviewWindow = lazy(() => import('./components/MonthlyHealthReviewWindow'));
const ReleaseOfInformationWindow = lazy(() => import('./components/ReleaseOfInformationWindow'));
const ReferralDetailsWindow = lazy(() => import('./components/ReferralDetailsWindow'));
const OutpatientIntakeConsentWindow = lazy(() => import('./components/OutpatientIntakeConsentWindow'));
const OutpatientIntakeFormWindow = lazy(() => import('./components/OutpatientIntakeFormWindow'));
const ColumbiaSuicideAssessmentWindow = lazy(() => import('./components/ColumbiaSuicideAssessmentWindow'));
const ConsentToTreatmentWindow = lazy(() => import('./components/ConsentToTreatmentWindow'));
const IntakeConsentViewerWindow = lazy(() => import('./components/IntakeConsentViewerWindow'));
const MiscellaneousDocumentWindow = lazy(() => import('./components/MiscellaneousDocumentWindow'));
const IncidentReportWindow = lazy(() => import('./components/IncidentReportWindow'));
const DAEndorsementWindow = lazy(() => import('./components/DAEndorsementWindow'));
const ClientFormPage = lazy(() => import('./components/waitlist/ClientFormPage'));
const ClientStatSurveyPage = lazy(() => import('./pages/public/ClientStatSurveyPage'));
const RpTimesheetSign = lazy(() => import('./pages/RpTimesheetSign'));
const RoiSign = lazy(() => import('./pages/RoiSign'));
const ConnectClaude = lazy(() => import('./pages/ConnectClaude'));
const NewHireProfile = lazy(() => import('./pages/NewHireProfile'));
const CaseManagerSign = lazy(() => import('./pages/CaseManagerSign'));
const LegalRepresentativeSign = lazy(() => import('./pages/LegalRepresentativeSign'));
// Pharmacy logins (User.accountKind 'pharmacy'): their own page, no admin shell.
const PharmacyPortal = lazy(() => import('./pages/pharmacy/PharmacyPortal'));
const IntakePacket = lazy(() => import('./pages/public/IntakePacket'));
const TelehealthGuestJoin = lazy(() => import('./pages/public/TelehealthGuestJoin'));
const ClientSign = lazy(() => import('./pages/public/ClientSign'));
const DemoBooking = lazy(() => import('./pages/public/DemoBooking'));
const DemoCancel = lazy(() =>
  import('./pages/public/DemoBooking').then((m) => ({ default: m.DemoCancel }))
);
const MHTreatmentPlanWindow = lazy(() => import('./components/MHTreatmentPlanWindow'));
const HIPAAPrivacyNoticeWindow = lazy(() => import('./components/HIPAAPrivacyNoticeWindow'));
const MHISReportWindow = lazy(() => import('./components/MHISReportWindow'));
const MHISSubmission = lazy(() => import('./admin/MHISSubmission'));
const ARMHSCommunityInterventionWindow = lazy(() => import('./components/ARMHSCommunityInterventionWindow'));
const FAITPContinuedNoteWindow = lazy(() => import('./components/FAITPContinuedNoteWindow'));
const ARMHSGroupNoteWindow = lazy(() => import('./components/ARMHSGroupNoteWindow'));
const ARMHSDischargeSummaryWindow = lazy(() => import('./components/ARMHSDischargeSummaryWindow'));
const BHCrisisPlanWindow = lazy(() => import('./components/BHCrisisPlanWindow'));
const ClinicalSupervisionSessionWindow = lazy(() => import('./components/ClinicalSupervisionSessionWindow'));
const CancelNoShowWindow = lazy(() => import('./components/CancelNoShowWindow'));
const ContactNoteWindow = lazy(() => import('./components/ContactNoteWindow'));
const CoResponderRoiFormWindow = lazy(() => import('./components/coresponder/roi/CoResponderRoiFormWindow'));
const CollateralNoteWindow = lazy(() => import('./components/CollateralNoteWindow'));
const CaseConsultNoteWindow = lazy(() => import('./components/CaseConsultNoteWindow'));
const StanleyBrownSafetyPlanWindow = lazy(() => import('./components/StanleyBrownSafetyPlanWindow'));
const AgencyFaceSheetWindow = lazy(() => import('./components/AgencyFaceSheetWindow'));
const VulnerableAdultRiskAssessmentWindow = lazy(() => import('./components/VulnerableAdultRiskAssessmentWindow'));
const ADSFormWindow = lazy(() => import('./components/ADSFormWindow'));
const ViewADSFormWindow = lazy(() => import('./components/ViewADSFormWindow'));
const PatientProfileWindow = lazy(() => import('./components/PatientProfileWindow'));

// Home Health Window Components
const SupervisionWindow = lazy(() => import('./components/homehealth/SupervisionWindow'));
const CarePlanWindow = lazy(() => import('./components/homehealth/CarePlanWindow'));
const FaceSheetWindow = lazy(() => import('./components/homehealth/FaceSheetWindow'));
const BillOfRightsWindow = lazy(() => import('./components/homehealth/BillOfRightsWindow'));
const HomeCareBillOfRightsWindow = lazy(() => import('./components/homehealth/HomeCareBillOfRightsWindow'));
const CountyROIWindow = lazy(() => import('./components/homehealth/CountyROIWindow'));
const GeneralPCAROIWindow = lazy(() => import('./components/homehealth/GeneralPCAROIWindow'));
const ParticipantRepWindow = lazy(() => import('./components/homehealth/ParticipantRepWindow'));
const DischargeSummaryWindow = lazy(() => import('./components/homehealth/DischargeSummaryWindow'));

// Payer-specific ROI Window Components
const WrightCountyROIWindow = lazy(() => import('./components/roi/WrightCountyROIWindow'));
const SherburneCountyROIWindow = lazy(() => import('./components/roi/SherburneCountyROIWindow'));
const UCareROIWindow = lazy(() => import('./components/roi/UCareROIWindow'));
const BentonCountyROIWindow = lazy(() => import('./components/roi/BentonCountyROIWindow'));
const BluestoneROIWindow = lazy(() => import('./components/roi/BluestoneROIWindow'));
const StearnsCountyROIWindow = lazy(() => import('./components/roi/StearnsCountyROIWindow'));
const HayatClinicROIWindow = lazy(() => import('./components/roi/HayatClinicROIWindow'));
const SimplicityHealthROIWindow = lazy(() => import('./components/roi/SimplicityHealthROIWindow'));
const CentraCareROIWindow = lazy(() => import('./components/roi/CentraCareROIWindow'));
const TelehealthSessionWindow = lazy(() => import('./components/telehealth/TelehealthSessionWindow'));

// Patient Portal page components (lazy-loaded individually)
const PatientPortalLayout = lazy(() => import('./patient-portal/PatientPortalLayout'));
const PatientLogin = lazy(() => import('./patient-portal/PatientLogin'));
const PatientActivation = lazy(() => import('./patient-portal/PatientActivation'));
const PatientDashboard = lazy(() => import('./patient-portal/PatientDashboard'));
const PatientAppointments = lazy(() => import('./patient-portal/PatientAppointments'));
const PatientDocuments = lazy(() => import('./patient-portal/PatientDocuments'));
const PatientProfile = lazy(() => import('./patient-portal/PatientProfile'));

// Test pages (lazy-loaded — only used in dev)
// Test pages removed: SessionTest, InactivityTest, InactivityDebug (dev-only, no routes)

// Component to manage page titles throughout the app
function PageTitleManager() {
  usePageTitle();
  return null;
}

// Create a QueryClient instance with optimized configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data is considered fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // React Query v5: retain inactive data for 10 minutes
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch when network reconnects
      retry: 1, // Retry failed requests once
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Writes are not safe to replay unless that specific mutation and its
      // backend endpoint implement an idempotency contract.
      retry: false,
    },
  },
});

function ThemedToastContainer() {
  const { effectiveTheme } = useTheme();
  return (
    <ToastContainer
      position="top-right"
      className="arasync-toasts"
      autoClose={3000}
      theme={effectiveTheme}
      style={{
        '--toastify-color-light': 'var(--color-surface)',
        '--toastify-color-dark': 'var(--color-surface)',
        '--toastify-text-color-light': 'var(--color-text)',
        '--toastify-text-color-dark': 'var(--color-text)',
      }}
    />
  );
}

function App() {
  return (
    <>
      <SkipNavigation />
      <QueryClientProvider client={queryClient}>
      <LiveRegionProvider>
      <ThemeProvider>
        <AuthProvider>
          <IntegratedSidebarProvider>
          <FeatureVisibilityProvider>
          <AccessControlProvider>
            <UserManagementProvider>
              <AlertsProvider>
              <NotificationsProvider>
              <Router>
            <PageTitleManager />
            <Suspense fallback={<LazyLoadFallback />}>
            <Routes>
              <Route path="/" element={<RoleRedirect />} />
              <Route path="/login" element={<Login />} />
              {/* AraSync Issues connector consent page. Checks its own sign-in (see the page header). */}
              <Route path="/connect/claude" element={<ConnectClaude />} />
              <Route path="/payment/success" element={<PaymentResultPage />} />
              <Route path="/payment/cancel" element={<PaymentResultPage cancelled />} />
              <Route path="/pay/:token" element={<InvoicePayPage />} />

              {/* Payer Detail Popup Window - No admin shell, opened from invoice generator */}
              <Route path="/payer-detail" element={<PermissionProtectedRoute requiredAnyPermissions={['view_billing']}><PayerDetailWindow /></PermissionProtectedRoute>} />

              {/* Platform Admin Routes - Super Admin Only */}
              <Route path="/platform" element={<ProtectedRoute requiredRole="super_admin" platformOnly><AdminShell /></ProtectedRoute>}>
                <Route index element={<PlatformDashboard />} />
                <Route path="organizations" element={<OrganizationsListView />} />
                {/* Cross-tenant support desk. Lives in the platform console because
                    a platform admin has no organizationId and cannot use the
                    tenant-scoped /admin/issues page. */}
                <Route path="support" element={<IssueReports />} />
                <Route path="live-support" element={<PlatformLiveSupport />} />
                <Route path="demo-scheduling" element={<PlatformDemoScheduling />} />
                <Route path="crm" element={<PlatformCrmPage />} />
                {/* Cross-tenant product usage. Platform console for the same
                    reason as the support desk: the backing endpoint is
                    platform-only and refuses an org-scoped account. */}
                <Route path="usage-analytics" element={<UsageAnalytics />} />
                <Route path="friction-analysis" element={<FrictionAnalytics />} />
                {/* Cross-tenant AI calls, tokens and cost per organization (platform-only endpoint). */}
                <Route path="ai-usage" element={<AiUsage />} />
                {/* Former dashboard-only modals, now first-class console pages. */}
                <Route path="system-health" element={<SystemHealthPage />} />
                <Route path="admins" element={<PlatformAdminsPage />} />
                <Route path="branding" element={<PlatformBrandingPage />} />
                <Route path="billing" element={<PlatformBillingPage />} />
                <Route path="app-updates" element={<AppUpdatesPage />} />
              </Route>

              {/* Admin/desktop routes - all wrapped in AdminShell */}
              <Route path="/admin" element={<ProtectedRoute><AdminShell /></ProtectedRoute>}>
                <Route path="ceo" element={<CeoWorkspace />} />
                <Route path="exclusion-checks" element={<WeCareHrRoute><PermissionProtectedRoute requiredPermission="manage_exclusion_checks"><ExclusionChecks /></PermissionProtectedRoute></WeCareHrRoute>} />
                <Route path="people-hr" element={<WeCareHrRoute><PermissionProtectedRoute requiredAnyPermissions={['manage_hr_reviews', 'manage_hr_compensation', 'manage_hr_lifecycle']} fallbackPath="/admin/people-hr/my-goals"><PeopleHr /></PermissionProtectedRoute></WeCareHrRoute>} />
                <Route path="people-hr/my-goals" element={<WeCareHrRoute><PermissionProtectedRoute requiredPermission="view_my_goals"><MyGoals /></PermissionProtectedRoute></WeCareHrRoute>} />
                <Route path="people-hr/applicants" element={<WeCareHrRoute><PermissionProtectedRoute requiredPermission="manage_hr_lifecycle"><HrApplicants /></PermissionProtectedRoute></WeCareHrRoute>} />
                <Route path="people-hr/new-hire" element={<WeCareHrRoute><PermissionProtectedRoute requiredPermission="manage_hr_lifecycle"><HrNewHireStart /></PermissionProtectedRoute></WeCareHrRoute>} />
                <Route path="people-hr/new-hire/:id" element={<WeCareHrRoute><PermissionProtectedRoute requiredPermission="manage_hr_lifecycle"><HrNewHireReview /></PermissionProtectedRoute></WeCareHrRoute>} />
                <Route index element={<AdminDashboard />} />
                {/* Co-Responder flattened routes (for single-bundle co-responder orgs) */}
                <Route path="caseload" element={<BundleProtectedRoute requiredBundle="mh-co-responder"><PermissionProtectedRoute requiredAnyPermissions={['view_patients']}><CoResponderCaseloadPage /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="co-responder-documents" element={<BundleProtectedRoute requiredBundle="mh-co-responder"><PermissionProtectedRoute requiredAnyPermissions={['view_co_responder_documents', 'manage_co_responder_documents', 'clinical_access']}><CoResponderDocumentsPage /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="community-providers" element={<BundleProtectedRoute requiredBundle="mh-co-responder"><PermissionProtectedRoute requiredAnyPermissions={['view_co_responder_documents', 'clinical_access']}><CommunityProvidersPage /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="encampments" element={<BundleProtectedRoute requiredBundle="mh-co-responder"><PermissionProtectedRoute requiredAnyPermissions={['view_encampments', 'manage_encampments']}><EncampmentsPage /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="programs" element={<PermissionProtectedRoute requiredSection="programs"><Programs /></PermissionProtectedRoute>} />
                <Route path="offices" element={<PermissionProtectedRoute requiredAnyPermissions={['manage_offices', 'manage_organization']}><OfficeManagement /></PermissionProtectedRoute>} />
                <Route path="programs/pca" element={<BundleProtectedRoute requiredBundle="home-health"><PersonalCareProgram /></BundleProtectedRoute>} />
                {/* Adult Day Services - Various slug patterns */}
                <Route path="programs/wecare-adult-day-services" element={<BundleProtectedRoute requiredBundle="adult-day-services"><AdultDayServicesProgramWrapper /></BundleProtectedRoute>} />
                <Route path="programs/adult-day-services" element={<BundleProtectedRoute requiredBundle="adult-day-services"><AdultDayServicesProgramWrapper /></BundleProtectedRoute>} />
                {/* Behavioral Health - Various slug patterns */}
                <Route path="programs/wecare-behavioral-health" element={<BundleProtectedRoute requiredBundle="behavioral-health"><BehavioralHealthProgram /></BundleProtectedRoute>} />
                <Route path="programs/behavioral-health" element={<BundleProtectedRoute requiredBundle="behavioral-health"><BehavioralHealthProgram /></BundleProtectedRoute>} />
                {/* Home Health / Personal Care - Various slug patterns */}
                <Route path="programs/wecare-home-health" element={<BundleProtectedRoute requiredBundle="home-health"><HomeHealthProgram /></BundleProtectedRoute>} />
                <Route path="programs/home-health" element={<BundleProtectedRoute requiredBundle="home-health"><HomeHealthProgram /></BundleProtectedRoute>} />
                {/* 245D Waiver Services */}
                <Route path="programs/245d-readiness" element={<BundleProtectedRoute requiredBundle="245d-waiver-services"><PermissionProtectedRoute requiredAllPermissions={['view_245d_basic', 'view_patients']}><Program245DReadinessDashboard /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="programs/245d-basic" element={<BundleProtectedRoute requiredBundle="245d-waiver-services"><Program245DBasic /></BundleProtectedRoute>} />
                <Route path="reports/245d" element={<BundleProtectedRoute requiredBundle="245d-waiver-services"><div className="p-6"><SupportReportsWorkspace type="245d" /></div></BundleProtectedRoute>} />
                <Route path="reports/residential" element={<BundleProtectedRoute requiredBundle="residential-services"><div className="p-6"><SupportReportsWorkspace type="residential" /></div></BundleProtectedRoute>} />
                <Route path="programs/245d-basic-support-services" element={<BundleProtectedRoute requiredBundle="245d-waiver-services"><Program245DBasic /></BundleProtectedRoute>} />
                <Route path="programs/245d-intensive" element={<BundleProtectedRoute requiredBundle="245d-waiver-services"><Program245DIntensive /></BundleProtectedRoute>} />
                <Route path="programs/245d-intensive-support-services" element={<BundleProtectedRoute requiredBundle="245d-waiver-services"><Program245DIntensive /></BundleProtectedRoute>} />
                <Route path="programs/residential-services" element={<BundleProtectedRoute requiredBundle="residential-services"><ResidentialServicesProgram /></BundleProtectedRoute>} />
                <Route path="programs/case-management" element={<BundleProtectedRoute requiredBundle="case-management"><CaseManagementProgram /></BundleProtectedRoute>} />
                {/* Minnesota Housing Support (chapter 256I): standalone bundle */}
                <Route path="programs/housing-support" element={<BundleProtectedRoute requiredBundle="housing-support"><HousingSupportProgram /></BundleProtectedRoute>} />
                {/* Time Tracking. Home Health included: PCAs see their EVV visit hours
                    here (time-tracking.js visit view); before 2026-10-07 the route
                    bounced them because they hold only Home Health program access. */}
                <Route path="time-tracking" element={
                  <BundleProtectedRoute requiredBundle={['behavioral-health', '245d-waiver-services', 'adult-day-services', 'home-health']}>
                    <PermissionProtectedRoute requiredAnyPermissions={['access_time_tracking', 'view_all_time_entries', 'manage_all_time_entries']}><TimeTrackingPage /></PermissionProtectedRoute>
                  </BundleProtectedRoute>
                } />
                {/* Appointments Management */}
                <Route path="appointments" element={
                  <BundleProtectedRoute requiredBundle="behavioral-health">
                    <PermissionProtectedRoute requiredSection="appointments"><AppointmentsPage /></PermissionProtectedRoute>
                  </BundleProtectedRoute>
                } />
                {/* Clinical Chart Report */}
                <Route path="clinical-chart-report" element={
                  <BundleProtectedRoute requiredBundle={['behavioral-health', '245d-waiver-services']}>
                    <PermissionProtectedRoute requiredSection="clinicalChart"><ClinicalChartReport /></PermissionProtectedRoute>
                  </BundleProtectedRoute>
                } />
                {/* Fallback dynamic route for other programs */}
                <Route path="programs/:programId" element={<PermissionProtectedRoute requiredAnyPermissions={['view_patients']}><ProgramView /></PermissionProtectedRoute>} />
                {/* DocumentManagement route removed — page not wired into sidebar */}
                <Route path="scheduler" element={<BundleProtectedRoute requiredBundle="home-health"><PermissionProtectedRoute requiredAnyPermissions={['view_schedules']}><EnhancedScheduler /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="alerts" element={<PermissionProtectedRoute requiredAnyPermissions={['view_notifications']}><AlertsPage /></PermissionProtectedRoute>} />
                {/* Retired. /admin/visit-logs mounted the very same <VisitLogs /> as the
                    Home Health program's Visit Logs tab — one page reachable at two URLs,
                    with no sidebar entry of its own. Kept as a redirect rather than deleted
                    so existing bookmarks and browser history resolve instead of 404ing. */}
                <Route path="visit-logs" element={<BundleProtectedRoute requiredBundle="home-health"><Navigate to="/admin/programs/home-health?tab=visit-logs" replace /></BundleProtectedRoute>} />
                <Route path="visits-today" element={<BundleProtectedRoute requiredBundle="home-health"><PermissionProtectedRoute requiredAnyPermissions={['view_visits']}><VisitsToday /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="visits/:visitId" element={<BundleProtectedRoute requiredBundle="home-health"><PermissionProtectedRoute requiredAnyPermissions={['view_visits']}><VisitDetailPage /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="calendar" element={<BundleProtectedRoute requiredBundle="home-health"><PermissionProtectedRoute requiredAnyPermissions={['view_schedules']}><VisitCalendar /></PermissionProtectedRoute></BundleProtectedRoute>} />

                {/* Caregivers - Permission Protected */}
                <Route path="caregivers" element={
                  <PermissionProtectedRoute requiredSection="providers">
                    <CaregiverSearch />
                  </PermissionProtectedRoute>
                } />

                {/* Patients - Permission Protected */}
                <Route path="patients" element={
                  <PermissionProtectedRoute requiredSection="patients">
                    <Patients />
                  </PermissionProtectedRoute>
                } />

                {/* Waitlist Management - Aggregated view across all programs */}
                <Route path="waitlist" element={<ExternalIntegrationProtectedRoute requiredIntegration="websiteIntegration"><PermissionProtectedRoute requiredAnyPermissions={['view_waitlist', 'manage_waitlist']}><WaitlistManagement /></PermissionProtectedRoute></ExternalIntegrationProtectedRoute>} />

                {/* Referral Management - Review incoming public referrals */}
                <Route path="referrals" element={<ExternalIntegrationProtectedRoute requiredIntegration="websiteIntegration"><PermissionProtectedRoute requiredAnyPermissions={['view_referrals', 'manage_referrals']}><ReferralManagement /></PermissionProtectedRoute></ExternalIntegrationProtectedRoute>} />

                {/* Intake Pipeline - Kanban board view of the referral-to-service pipeline */}
                <Route path="intake-pipeline" element={<ExternalIntegrationProtectedRoute requiredIntegration="websiteIntegration"><PermissionProtectedRoute requiredAnyPermissions={['view_referrals', 'manage_referrals']}><IntakePipeline /></PermissionProtectedRoute></ExternalIntegrationProtectedRoute>} />

                {/* Caregiver Requests - Admin approval queue for schedule changes and missed clock-outs */}
                {/* Home Health, or a 245D tenant reviewing EVV time corrections only (the page draws that view). */}
                <Route path="caregiver-requests" element={<BundleProtectedRoute requiredBundle={['home-health', '245d-waiver-services']}><PermissionProtectedRoute requiredAnyPermissions={['edit_schedules', 'edit_visits', 'review_245d_service_notes']}><CaregiverRequests /></PermissionProtectedRoute></BundleProtectedRoute>} />

                {/* DA Scheduling - Publish and manage Diagnostic Assessment slots */}
                <Route path="da-scheduling" element={<BundleProtectedRoute requiredBundle="behavioral-health"><ExternalIntegrationProtectedRoute requiredIntegration="websiteIntegration"><PermissionProtectedRoute requiredAnyPermissions={['manage_da_scheduling', 'manage_da_scheduling_all', 'manage_referrals']}><DAScheduling /></PermissionProtectedRoute></ExternalIntegrationProtectedRoute></BundleProtectedRoute>} />

                <Route path="contacts" element={<PermissionProtectedRoute requiredAnyPermissions={['view_contacts', 'manage_contacts']}><ContactsList /></PermissionProtectedRoute>} />
                <Route path="resources" element={<PermissionProtectedRoute requiredPermission="view_resources"><Resources /></PermissionProtectedRoute>} />
                {/* Training Library (2026-10-07): how-to videos, streamed only. The server
                    filters each list to the videos whose audience includes the viewer. */}
                <Route path="training" element={<PermissionProtectedRoute requiredAnyPermissions={['view_training_library', 'manage_training_library']} fallbackRole={['admin', 'super_admin']}><TrainingLibrary /></PermissionProtectedRoute>} />
                <Route path="training/:videoId" element={<PermissionProtectedRoute requiredAnyPermissions={['view_training_library', 'manage_training_library']} fallbackRole={['admin', 'super_admin']}><TrainingPlayer /></PermissionProtectedRoute>} />
                <Route path="visit-management" element={<BundleProtectedRoute requiredBundle="home-health"><PermissionProtectedRoute requiredAnyPermissions={['view_visit_templates']}><VisitManagement /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="visit-management/service-codes" element={<BundleProtectedRoute requiredBundle="home-health"><PermissionProtectedRoute requiredAnyPermissions={['view_visits', 'edit_service_codes', 'view_service_codes']}><ServiceCodesManagement /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="visit-management/visit-notes" element={<BundleProtectedRoute requiredBundle="home-health"><PermissionProtectedRoute requiredAnyPermissions={['view_visits']}><VisitNotesManagement /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="visit-management/quality-reviews" element={<BundleProtectedRoute requiredBundle="home-health"><PermissionProtectedRoute requiredAnyPermissions={['view_quality_reviews']}><QualityReviewsManagement /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="visit-management/missed-visits" element={<BundleProtectedRoute requiredBundle="home-health"><PermissionProtectedRoute requiredAnyPermissions={['view_visits']}><MissedVisitManagement /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="hha-status" element={
                  <ExternalIntegrationProtectedRoute requiredIntegration="hhaexchange">
                    <BundleProtectedRoute requiredBundle="home-health"><ErrorBoundary><PermissionProtectedRoute requiredAnyPermissions={['view_visits']}><HHAStatusDashboard /></PermissionProtectedRoute></ErrorBoundary></BundleProtectedRoute>
                  </ExternalIntegrationProtectedRoute>
                } />
                <Route path="visit-history" element={<BundleProtectedRoute requiredBundle="home-health"><PermissionProtectedRoute requiredAnyPermissions={['view_visits']}><VisitHistory /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="profile" element={<Profile />} />
                <Route path="settings" element={
                  <PermissionProtectedRoute requiredPermission="manage_organization">
                    <Settings />
                  </PermissionProtectedRoute>
                } />
                <Route path="settings/timezone" element={<SimplifiedTimezoneManager />} />
                <Route path="settings/signature" element={<SignatureManager />} />
                <Route path="notification-settings" element={<PermissionProtectedRoute requiredPermission="manage_notifications"><NotificationSettings /></PermissionProtectedRoute>} />
                {/* One address per report (2026-09-29): the list at reports, each report at
                    reports/<id>, one route so the page stays mounted between them. */}
                <Route path="reports/:reportId?" element={
                  <PermissionProtectedRoute requiredSection="reports">
                    <Reports />
                  </PermissionProtectedRoute>
                } />
                {/* The org-wide MHIS file needs its report switch, not only clinical access (2026-09-29). */}
                <Route path="reports/mhis" element={<BundleProtectedRoute requiredBundle="behavioral-health"><PermissionProtectedRoute requiredPermission="report_mhis_submission"><MHISSubmission /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="analytics" element={<BundleProtectedRoute requiredBundle={['behavioral-health', 'home-health', 'adult-day-services', '245d-waiver-services', 'mh-co-responder', 'residential-services']}><PermissionProtectedRoute requiredAnyPermissions={['view_analytics', 'edit_analytics']}><ModernAnalytics /></PermissionProtectedRoute></BundleProtectedRoute>} />
                {/* modern-analytics redirect removed — legacy URL no longer needed */}
                <Route path="service-lines" element={
                  <BundleProtectedRoute requiredBundle="billing">
                    <PermissionProtectedRoute requiredAnyPermissions={['view_service_lines', 'manage_service_lines', 'edit_service_lines', 'view_home_health_service_lines']}>
                      <ServiceLinesOptimized />
                    </PermissionProtectedRoute>
                  </BundleProtectedRoute>
                } />
                <Route path="communication" element={
                  <PermissionProtectedRoute requiredSection="communication">
                    <Communication />
                  </PermissionProtectedRoute>
                } />
                <Route path="issues" element={<IssueReports />} />
                <Route path="announcements" element={<PermissionProtectedRoute requiredPermission="manage_notifications"><Announcements /></PermissionProtectedRoute>} />
                <Route path="sms-consent" element={<ExternalIntegrationProtectedRoute requiredIntegration="textAlerts"><ProtectedRoute requiredRole={["admin", "super_admin"]}><SmsConsent /></ProtectedRoute></ExternalIntegrationProtectedRoute>} />
                <Route path="text-alerts" element={<ExternalIntegrationProtectedRoute requiredIntegration="textAlerts"><TextAlerts /></ExternalIntegrationProtectedRoute>} />
                <Route path="on-call" element={<PermissionProtectedRoute requiredPermission="manage_notifications"><OnCallSettings /></PermissionProtectedRoute>} />
                <Route path="fax" element={
                  <ExternalIntegrationProtectedRoute requiredIntegration="fax">
                    <PermissionProtectedRoute
                      requiredAnyPermissions={['fax_view', 'fax_send']}
                      fallbackRole={['admin', 'super_admin']}
                    >
                      <FaxCenter />
                    </PermissionProtectedRoute>
                  </ExternalIntegrationProtectedRoute>
                } />
                <Route path="eligibility" element={
                  <BundleProtectedRoute requiredBundle="billing">
                    <ExternalIntegrationProtectedRoute requiredAnyIntegration={['mnits', 'availity']}>
                      <PermissionProtectedRoute requiredAnyPermissions={['check_eligibility', 'view_eligibility']}>
                        <EligibilityCheck />
                      </PermissionProtectedRoute>
                    </ExternalIntegrationProtectedRoute>
                  </BundleProtectedRoute>
                } />

                {/* Billing Routes - Permission Protected */}
                <Route path="billing" element={
                  <PermissionProtectedRoute requiredAnyPermissions={['view_billing', 'manage_billing']}>
                    <Billing />
                  </PermissionProtectedRoute>
                } />
                <Route path="billing/invoice-generator" element={
                  <PermissionProtectedRoute requiredSection="billing">
                    <InvoiceGeneratorEnhanced />
                  </PermissionProtectedRoute>
                } />
                <Route path="billing/batches" element={
                  <PermissionProtectedRoute requiredSection="billing">
                    <BillingBatches />
                  </PermissionProtectedRoute>
                } />
                <Route path="billing/claim-status" element={
                  <PermissionProtectedRoute requiredSection="billing">
                    <ClaimStatusPage />
                  </PermissionProtectedRoute>
                } />
                <Route path="billing/service-codes" element={
                  <PermissionProtectedRoute requiredSection="billing">
                    <ServiceCodesManagement />
                  </PermissionProtectedRoute>
                } />
                <Route path="billing/payers" element={
                  <PermissionProtectedRoute requiredSection="billing">
                    <PayerManagement />
                  </PermissionProtectedRoute>
                } />
                <Route path="billing/office-ally" element={
                  <ExternalIntegrationProtectedRoute requiredIntegration="officeAlly">
                    <PermissionProtectedRoute requiredSection="billing">
                      <OfficeAllyIntegration />
                    </PermissionProtectedRoute>
                  </ExternalIntegrationProtectedRoute>
                } />
                <Route path="billing/reports" element={
                  <PermissionProtectedRoute requiredSection="billing">
                    <BillingReports />
                  </PermissionProtectedRoute>
                } />
                <Route path="billing/era-reconciliation" element={
                  <PermissionProtectedRoute requiredSection="billing">
                    <ERAReconciliation />
                  </PermissionProtectedRoute>
                } />
                <Route path="billing/client-statement" element={
                  <PermissionProtectedRoute requiredSection="billing">
                    <ClientStatement />
                  </PermissionProtectedRoute>
                } />
                <Route path="billing/grant-invoices" element={
                  <OrgFeatureProtectedRoute requiredFeature="camhi_grant_billing">
                    <PermissionProtectedRoute requiredSection="billing">
                      <GrantInvoices />
                    </PermissionProtectedRoute>
                  </OrgFeatureProtectedRoute>
                } />
                <Route path="billing/grant-report" element={
                  <OrgFeatureProtectedRoute requiredFeature="camhi_grant_billing">
                    <PermissionProtectedRoute requiredSection="billing">
                      <GrantReport />
                    </PermissionProtectedRoute>
                  </OrgFeatureProtectedRoute>
                } />
                <Route path="billing/billing-agent" element={
                  <OrgFeatureProtectedRoute requiredFeature="billing_agent">
                    <PermissionProtectedRoute requiredSection="billing">
                      <BillingAgentDashboard />
                    </PermissionProtectedRoute>
                  </OrgFeatureProtectedRoute>
                } />
                <Route path="billing/denials" element={
                  <PermissionProtectedRoute requiredSection="billing">
                    <DenialManagementPage />
                  </PermissionProtectedRoute>
                } />

                {/* Accounting */}
                <Route path="accounting" element={
                  <BundleProtectedRoute requiredBundle="billing">
                    <PermissionProtectedRoute requiredAnyPermissions={['view_accounting', 'export_accounting']}>
                      <AccountingLedger />
                    </PermissionProtectedRoute>
                  </BundleProtectedRoute>
                } />

                <Route path="compliance" element={<Navigate to="/admin/compliance/overview" replace />} />
                {/* Each compliance page checks the same permission as its sidebar link and API. */}
                <Route path="compliance/audit-logs" element={
                  <PermissionProtectedRoute requiredPermission="view_audit_logs">
                    <ModernAuditLogs />
                  </PermissionProtectedRoute>
                } />
                <Route path="compliance/data-readiness" element={
                  <BundleProtectedRoute requiredBundle="billing">
                    <PermissionProtectedRoute requiredPermission="view_data_readiness"><DataReadiness /></PermissionProtectedRoute>
                  </BundleProtectedRoute>
                } />
                <Route path="compliance/document-expiration" element={
                  <PermissionProtectedRoute requiredPermission="view_document_expiration">
                    <DocumentExpirationDashboard />
                  </PermissionProtectedRoute>
                } />
                <Route path="compliance/reports" element={<Navigate to="/admin/reports" replace />} />
                {/* Staff credentials: view_staff_credentials only. manage_users was
                    also accepted here, which let a care coordinator into staff HR
                    records; every role that legitimately needs this already holds
                    view_staff_credentials directly. */}
                <Route path="compliance/staff-credentials" element={
                  <PermissionProtectedRoute requiredAnyPermissions={['view_staff_credentials']}>
                    <StaffCredentialManagement />
                  </PermissionProtectedRoute>
                } />
                <Route path="compliance/authorization-guardrails" element={
                  <PermissionProtectedRoute requiredPermission="view_auth_guardrails">
                    <AuthorizationGuardrails />
                  </PermissionProtectedRoute>
                } />
                <Route path="compliance/rule-engine" element={
                  <PermissionProtectedRoute requiredPermission="view_rule_engine">
                    <ComplianceRuleEngine />
                  </PermissionProtectedRoute>
                } />
                {/* Same bundles as its sidebar link and API (bundleCapabilities
                    'compliance.narrativeSimilarity', app.js requireBundle). */}
                <Route path="compliance/narrative-similarity" element={
                  <BundleProtectedRoute requiredBundle={['behavioral-health', '245d-waiver-services', 'mh-co-responder']}>
                    <PermissionProtectedRoute requiredPermission="view_narrative_similarity">
                      <NarrativeSimilarityFindings />
                    </PermissionProtectedRoute>
                  </BundleProtectedRoute>
                } />
                {/* Same bundle as its sidebar link and API (bundleCapabilities
                    'compliance.recordsRequests', app.js requireBundle). */}
                <Route path="compliance/records-requests" element={
                  <BundleProtectedRoute requiredBundle={['behavioral-health']}><PermissionProtectedRoute requiredPermission="manage_records_requests"><RecordsRequestsPage /></PermissionProtectedRoute></BundleProtectedRoute>
                } />
                <Route path="compliance/records-requests/:id" element={
                  <BundleProtectedRoute requiredBundle={['behavioral-health']}><PermissionProtectedRoute requiredPermission="manage_records_requests"><RecordsRequestDetail /></PermissionProtectedRoute></BundleProtectedRoute>
                } />
                <Route path="compliance/billing-compliance" element={
                  <PermissionProtectedRoute requiredPermission="view_billing_compliance">
                    <BillingComplianceDashboard />
                  </PermissionProtectedRoute>
                } />
                <Route path="compliance/overview" element={
                  <PermissionProtectedRoute requiredPermission="view_compliance_overview">
                    <ComplianceOverviewDashboard />
                  </PermissionProtectedRoute>
                } />
                <Route path="compliance/pca-supervision" element={
                  <BundleProtectedRoute requiredBundle="home-health">
                    <PermissionProtectedRoute requiredPermission="manage_compliance">
                      <PCASupervisionTracking />
                    </PermissionProtectedRoute>
                  </BundleProtectedRoute>
                } />
                <Route path="worker-training" element={
                  <BundleProtectedRoute requiredBundle="home-health">
                    <PermissionProtectedRoute requiredAnyPermissions={['view_worker_training', 'perform_visits']}>
                      <WorkerTrainingRedirect />
                    </PermissionProtectedRoute>
                  </BundleProtectedRoute>
                } />

                {/* Clinical Routes - Permission Protected */}
                {/* New unified Clinical page */}
                <Route path="clinical" element={
                  <ClinicalBundleGate><PermissionProtectedRoute requiredSection="clinical">
                    <ClinicalPage />
                  </PermissionProtectedRoute></ClinicalBundleGate>
                } />
                {/* Legacy routes - kept for backward compatibility */}
                <Route path="clinical/treatment-plans" element={
                  <ClinicalBundleGate><PermissionProtectedRoute requiredSection="clinical">
                    <ClinicalTreatmentPlans />
                  </PermissionProtectedRoute></ClinicalBundleGate>
                } />
                <Route path="clinical/notes" element={
                  <ClinicalBundleGate><PermissionProtectedRoute requiredSection="clinical">
                    <ClinicalNotes />
                  </PermissionProtectedRoute></ClinicalBundleGate>
                } />
                <Route path="clinical/assessments" element={
                  <ClinicalBundleGate><PermissionProtectedRoute requiredSection="clinical">
                    <ClinicalAssessments />
                  </PermissionProtectedRoute></ClinicalBundleGate>
                } />
                <Route path="clinical/approvals" element={
                  <ClinicalBundleGate><PermissionProtectedRoute requiredAnyPermissions={['approve_documents', 'quality_reviews']}>
                    <ClinicalApprovalProtectedRoute>
                      <SupervisorApprovals />
                    </ClinicalApprovalProtectedRoute>
                  </PermissionProtectedRoute></ClinicalBundleGate>
                } />
                <Route path="clinical/signature-waivers" element={
                  <ClinicalBundleGate><PermissionProtectedRoute requiredAnyPermissions={['waive_client_time_attestation']}>
                    <ClientSignatureWaivers />
                  </PermissionProtectedRoute></ClinicalBundleGate>
                } />
                {/* Clinical Reports moved to /reports/clinical */}

                {/* Client Stat Surveys - Send satisfaction surveys and review responses */}
                <Route path="client-stat-surveys" element={
                  <PermissionProtectedRoute requiredAnyPermissions={['client_stat_surveys_manage']}>
                    <ClientStatSurveys />
                  </PermissionProtectedRoute>
                } />

                <Route path="system" element={
                  <OrgFeatureProtectedRoute requiredFeature="system_health">
                    <PermissionProtectedRoute requiredPermission="view_system">
                      <SystemHealth />
                    </PermissionProtectedRoute>
                  </OrgFeatureProtectedRoute>
                } />
                <Route path="ai-analytics" element={
                  <BundleProtectedRoute requiredBundle={['behavioral-health', 'home-health']}>
                    <ExternalIntegrationProtectedRoute requiredIntegration="azureAi">
                      <PermissionProtectedRoute requiredPermission="view_scheduling_intelligence" fallbackRole="super_admin"><AIAnalytics /></PermissionProtectedRoute>
                    </ExternalIntegrationProtectedRoute>
                  </BundleProtectedRoute>
                } />
                <Route path="clinical-imports" element={<BundleProtectedRoute requiredBundle="behavioral-health"><PermissionProtectedRoute requiredPermission="manage_clinical_imports"><ClinicalImports /></PermissionProtectedRoute></BundleProtectedRoute>} />
                <Route path="data-import" element={<PermissionProtectedRoute requiredAnyPermissions={['import_clients', 'import_staff', 'import_authorizations', 'import_client_documents']}><DataImportPage /></PermissionProtectedRoute>} />
                <Route path="knowledge-base" element={
                  <OrgFeatureProtectedRoute requiredFeature="knowledge_base">
                    <PermissionProtectedRoute requiredPermission="system_settings"><KnowledgeBase /></PermissionProtectedRoute>
                  </OrgFeatureProtectedRoute>
                } />
                <Route path="users" element={<PermissionProtectedRoute requiredPermission="manage_users"><SystemUsers /></PermissionProtectedRoute>} />
                <Route path="provider-types" element={<PermissionProtectedRoute requiredPermission="manage_providers"><ProviderTypeManagement /></PermissionProtectedRoute>} />
                {/* Old separated permission routes - replaced by integrated permissions */}
                {/* <Route path="sidebar-permissions" element={<ProtectedRoute requiredRole="super_admin"><SidebarPermissions /></ProtectedRoute>} /> */}
                {/* <Route path="rbac" element={<ProtectedRoute requiredRole="super_admin"><RolesPermissions /></ProtectedRoute>} /> */}
                {/* New integrated permissions route */}
                <Route path="permissions" element={<PermissionProtectedRoute requiredPermission="manage_system_permissions"><IntegratedPermissions /></PermissionProtectedRoute>} />
                <Route path="feature-visibility" element={<PermissionProtectedRoute requiredPermission="manage_access_rules"><FeatureVisibilityAdmin /></PermissionProtectedRoute>} />
                {/* Orphaned routes removed: missed-visits (duplicate of visit-management/missed-visits), session-test, inactivity-test, inactivity-debug (dev-only test pages) */}
                {/* Any other /admin address: a Not Found page inside the shell, not a blank page. Keep last. */}
                <Route path="*" element={<AdminNotFound />} />
              </Route>

              {/* Document Viewers - Outside admin shell for full-screen viewing */}
              <Route path="/progress-notes/:noteId" element={<ClinicalBundleGate><ProtectedRoute><ProgressNoteViewer /></ProtectedRoute></ClinicalBundleGate>} />
              <Route path="/health-reviews/:reviewId" element={<ClinicalBundleGate><ProtectedRoute><HealthReviewViewer /></ProtectedRoute></ClinicalBundleGate>} />
              <Route path="/ads-forms/:formId" element={<BundleProtectedRoute requiredBundle={ADS_FORM_BUNDLES}><ProtectedRoute><ADSFormViewer /></ProtectedRoute></BundleProtectedRoute>} />
              <Route path="/diagnostic-assessments/:assessmentId" element={<ClinicalBundleGate><ProtectedRoute><DiagnosticAssessmentViewer /></ProtectedRoute></ClinicalBundleGate>} />
              {/* View ADS Form Window - For opening completed forms in readonly popup */}
              <Route path="/view-ads-form" element={<BundleProtectedRoute requiredBundle={ADS_FORM_BUNDLES}><ViewADSFormWindow /></BundleProtectedRoute>} />
              {/* Patient Profile Window - For opening patient profiles in popup */}
              <Route path="/patient-profile" element={<PermissionProtectedRoute requiredAnyPermissions={['view_patients']}><PatientProfileWindow /></PermissionProtectedRoute>} />
              {/* ARMHS Progress Note Window - For opening in new window */}
              <Route path="/armhs-progress-note" element={<ClinicalBundleGate><ARMHSProgressNoteWindow /></ClinicalBundleGate>} />
              <Route path="/mh-progress-note" element={<ClinicalBundleGate><MHProgressNoteWindow /></ClinicalBundleGate>} />
              {/* Release of Information Window - For opening in new window */}
              <Route path="/release-of-information" element={
                <RepositoryRoiRouteGate><ReleaseOfInformationWindow /></RepositoryRoiRouteGate>
              } />
              {/* Payer-specific ROI Windows - For opening in new window */}
              <Route path="/ads-roi-wright-county" element={<BundleProtectedRoute requiredBundle="adult-day-services"><FormClientBoundary><WrightCountyROIWindow /></FormClientBoundary></BundleProtectedRoute>} />
              <Route path="/ads-roi-sherburne-county" element={<BundleProtectedRoute requiredBundle="adult-day-services"><FormClientBoundary><SherburneCountyROIWindow /></FormClientBoundary></BundleProtectedRoute>} />
              <Route path="/ads-roi-ucare" element={<BundleProtectedRoute requiredBundle="adult-day-services"><FormClientBoundary><UCareROIWindow /></FormClientBoundary></BundleProtectedRoute>} />
              <Route path="/ads-roi-benton-county" element={<BundleProtectedRoute requiredBundle="adult-day-services"><FormClientBoundary><BentonCountyROIWindow /></FormClientBoundary></BundleProtectedRoute>} />
              <Route path="/ads-roi-bluestone" element={
                <BundleProtectedRoute requiredBundle="adult-day-services"><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><FormClientBoundary><BluestoneROIWindow /></FormClientBoundary></OrgFeatureProtectedRoute></BundleProtectedRoute>
              } />
              <Route path="/ads-roi-stearns-county" element={
                <BundleProtectedRoute requiredBundle="adult-day-services"><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><FormClientBoundary><StearnsCountyROIWindow /></FormClientBoundary></OrgFeatureProtectedRoute></BundleProtectedRoute>
              } />
              <Route path="/ads-roi-hayat-clinic" element={
                <BundleProtectedRoute requiredBundle="adult-day-services"><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><FormClientBoundary><HayatClinicROIWindow /></FormClientBoundary></OrgFeatureProtectedRoute></BundleProtectedRoute>
              } />
              <Route path="/ads-roi-simplicity-health" element={
                <BundleProtectedRoute requiredBundle="adult-day-services"><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><FormClientBoundary><SimplicityHealthROIWindow /></FormClientBoundary></OrgFeatureProtectedRoute></BundleProtectedRoute>
              } />
              <Route path="/ads-roi-centracare" element={
                <BundleProtectedRoute requiredBundle="adult-day-services"><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><FormClientBoundary><CentraCareROIWindow /></FormClientBoundary></OrgFeatureProtectedRoute></BundleProtectedRoute>
              } />
              <Route path="/telehealth-session" element={
                <ProtectedRoute>
                  <ExternalIntegrationProtectedRoute requiredIntegration="telehealth">
                    <TelehealthSessionWindow />
                  </ExternalIntegrationProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Referral Details Window - For opening in popup */}
              <Route path="/referral-details" element={
                <ProtectedRoute>
                  <ExternalIntegrationProtectedRoute requiredIntegration="websiteIntegration">
                    <ReferralDetailsWindow />
                  </ExternalIntegrationProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Outpatient Intake & Consent Package Window */}
              <Route path="/outpatient-intake-consent" element={
                <ClinicalBundleGate><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><OutpatientIntakeConsentWindow /></OrgFeatureProtectedRoute></ClinicalBundleGate>
              } />
              <Route path="/client-intake-form" element={<ClinicalBundleGate><OutpatientIntakeFormWindow /></ClinicalBundleGate>} />
              {/* Legacy route for existing bookmarks/links */}
              <Route path="/armhs-intake-consent" element={
                <ClinicalBundleGate><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><OutpatientIntakeConsentWindow /></OrgFeatureProtectedRoute></ClinicalBundleGate>
              } />
              {/* MHIS Report Window - For opening in new window */}
              <Route path="/mhis-report" element={<BundleProtectedRoute requiredBundle="behavioral-health"><MHISReportWindow /></BundleProtectedRoute>} />
              {/* Policy Assistant - standalone owner-organization window */}
              <Route path="/policy-assistant" element={
                <ProtectedRoute>
                  <OrgFeatureProtectedRoute requiredFeature="knowledge_base">
                    <PolicyAssistantPage />
                  </OrgFeatureProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Adult Day Services Progress Note Window - For opening in new window */}
              <Route path="/ads-progress-note-window" element={<BundleProtectedRoute requiredBundle="adult-day-services"><FormClientBoundary><ADSProgressNoteWindow /></FormClientBoundary></BundleProtectedRoute>} />
              {/* ADS Form Window - For opening in new window */}
              <Route path="/ads-form" element={<BundleProtectedRoute requiredBundle={ADS_FORM_BUNDLES}><FormClientBoundary><ADSFormWindow /></FormClientBoundary></BundleProtectedRoute>} />
              {/* Assessment Windows - For opening in new window */}
              <Route path="/diagnostic-assessment" element={<ClinicalBundleGate><DiagnosticAssessmentWindow /></ClinicalBundleGate>} />
              <Route path="/mh-diagnostic-assessment" element={<ClinicalBundleGate><MHDiagnosticAssessmentWindow /></ClinicalBundleGate>} />
              <Route path="/standard-diagnostic-assessment" element={<ClinicalBundleGate><StandardDiagnosticAssessmentWindow /></ClinicalBundleGate>} />
              <Route path="/yz-caretap-diagnostic-assessment" element={
                <OrgFeatureProtectedRoute requiredFeature="yz_caretap_diagnostic_assessment">
                  <PermissionProtectedRoute requiredAnyPermissions={['clinical_access', 'view_assessments', 'complete_assessments']}><YZCareTapDiagnosticAssessmentWindow /></PermissionProtectedRoute>
                </OrgFeatureProtectedRoute>
              } />
              <Route path="/phq9-assessment" element={<ClinicalBundleGate><PHQ9AssessmentWindow /></ClinicalBundleGate>} />
              <Route path="/gad7-assessment" element={<ClinicalBundleGate><GAD7AssessmentWindow /></ClinicalBundleGate>} />
              <Route path="/pcl5-assessment" element={<ClinicalBundleGate><PCL5AssessmentWindow /></ClinicalBundleGate>} />
              <Route path="/functional-assessment" element={<ClinicalBundleGate><FunctionalAssessmentWindow /></ClinicalBundleGate>} />
              <Route path="/yz-caretap-functional-assessment" element={
                <OrgFeatureProtectedRoute requiredFeature="yz_caretap_functional_assessment">
                  <PermissionProtectedRoute requiredAnyPermissions={['clinical_access']}><YZCareTapFunctionalAssessmentWindow /></PermissionProtectedRoute>
                </OrgFeatureProtectedRoute>
              } />
              <Route path="/yz-functional-assessment" element={
                <OrgFeatureProtectedRoute requiredFeature="yz_functional_assessment">
                  <PermissionProtectedRoute requiredAnyPermissions={['clinical_access']}><YZFunctionalAssessmentWindow /></PermissionProtectedRoute>
                </OrgFeatureProtectedRoute>
              } />
              <Route path="/diagnostic-assessment-addendum" element={<ClinicalBundleGate><DiagnosticAssessmentAddendumWindow /></ClinicalBundleGate>} />
              <Route path="/iapp-addendum" element={<BundleProtectedRoute requiredBundle="home-health"><FormClientBoundary><IAPPAddendumWindow /></FormClientBoundary></BundleProtectedRoute>} />
              <Route path="/uploaded-diagnostic-assessments/:documentId" element={<PermissionProtectedRoute requiredAnyPermissions={['clinical_access']}><UploadedDiagnosticAssessmentViewer /></PermissionProtectedRoute>} />
              <Route path="/columbia-suicide-assessment" element={<ClinicalBundleGate><ColumbiaSuicideAssessmentWindow /></ClinicalBundleGate>} />
              <Route path="/consent-to-treatment" element={<ClinicalBundleGate><ConsentToTreatmentWindow /></ClinicalBundleGate>} />
              <Route path="/intake-consent-viewer" element={<ClinicalBundleGate><IntakeConsentViewerWindow /></ClinicalBundleGate>} />
              <Route path="/miscellaneous-document" element={<ClinicalBundleGate><MiscellaneousDocumentWindow /></ClinicalBundleGate>} />
              <Route path="/incident-report" element={<ClinicalBundleGate><IncidentReportWindow /></ClinicalBundleGate>} />
              <Route path="/da-endorsement" element={<ClinicalBundleGate><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><DAEndorsementWindow /></OrgFeatureProtectedRoute></ClinicalBundleGate>} />
              <Route path="/hipaa-privacy-notice" element={<ClinicalBundleGate><FormClientBoundary><HIPAAPrivacyNoticeWindow /></FormClientBoundary></ClinicalBundleGate>} />
              <Route path="/mh-treatment-plan-window" element={<ClinicalBundleGate><MHTreatmentPlanWindow /></ClinicalBundleGate>} />
              <Route path="/armhs-community-intervention" element={<ClinicalBundleGate><ARMHSCommunityInterventionWindow /></ClinicalBundleGate>} />
              <Route path="/fa-itp-continued-note" element={<ClinicalBundleGate><FAITPContinuedNoteWindow /></ClinicalBundleGate>} />
              <Route path="/armhs-group-note" element={<ClinicalBundleGate><ARMHSGroupNoteWindow /></ClinicalBundleGate>} />
              <Route path="/armhs-discharge-summary" element={<ClinicalBundleGate><ARMHSDischargeSummaryWindow /></ClinicalBundleGate>} />
              <Route path="/bh-crisis-plan" element={<ClinicalBundleGate><BHCrisisPlanWindow /></ClinicalBundleGate>} />
              {/* The address before the 2026-10-28 rename (ARMHS Crisis Plan): old links and open windows. */}
              <Route path="/armhs-crisis-plan" element={<RenamedRoute to="/bh-crisis-plan" />} />
              <Route path="/clinical-supervision-session" element={<ClinicalBundleGate><ClinicalSupervisionSessionWindow /></ClinicalBundleGate>} />
              <Route path="/cancel-no-show" element={<ClinicalBundleGate><CancelNoShowWindow /></ClinicalBundleGate>} />
              <Route path="/contact-note" element={<ClinicalBundleGate><ContactNoteWindow /></ClinicalBundleGate>} />
              {/* Partner-agency ROI forms (?type=ROI_..., optional &docId= to view) */}
              <Route path="/coresponder-roi-form" element={<BundleProtectedRoute requiredBundle="mh-co-responder"><CoResponderRoiFormWindow /></BundleProtectedRoute>} />
              {/* Collateral Note is a Behavioral Health document (ARA-1109), like
                  Contact Note and Cancel/No Show. It sat in the Co-Responder block
                  above, so a BH-only tenant was bounced back to the clinical page. */}
              <Route path="/collateral-note" element={<ClinicalBundleGate><CollateralNoteWindow /></ClinicalBundleGate>} />
              <Route path="/case-consult-note" element={<ClinicalBundleGate><CaseConsultNoteWindow /></ClinicalBundleGate>} />
              <Route path="/vulnerable-adult-risk-assessment" element={<ClinicalBundleGate><VulnerableAdultRiskAssessmentWindow /></ClinicalBundleGate>} />
              {/* Stanley-Brown is shared clinical safety planning; the Agency Face Sheet remains Co-Responder-only. */}
              <Route path="/stanley-brown-safety-plan" element={<ClinicalBundleGate><StanleyBrownSafetyPlanWindow /></ClinicalBundleGate>} />
              <Route path="/agency-face-sheet" element={<BundleProtectedRoute requiredBundle="mh-co-responder"><AgencyFaceSheetWindow /></BundleProtectedRoute>} />
              {/* Treatment Plan Window - For opening in new window */}
              <Route path="/treatment-plan-window" element={<ClinicalBundleGate><TreatmentPlanWindow /></ClinicalBundleGate>} />
              <Route path="/yz-caretap-treatment-plan" element={
                <OrgFeatureProtectedRoute requiredFeature="yz_caretap_treatment_plan">
                  <PermissionProtectedRoute requiredAnyPermissions={['clinical_access']}><YZCareTapTreatmentPlanWindow /></PermissionProtectedRoute>
                </OrgFeatureProtectedRoute>
              } />
              {/* Read-only progress note imported from CareTap (Y&Z only). */}
              <Route path="/yz-caretap-progress-note" element={
                <OrgFeatureProtectedRoute requiredFeature="yz_caretap_progress_note">
                  <PermissionProtectedRoute requiredAnyPermissions={['clinical_access']}><YZCareTapProgressNoteWindow /></PermissionProtectedRoute>
                </OrgFeatureProtectedRoute>
              } />
              {/* AI Compliance Scan Results Window - For opening in new window */}
              <Route path="/ai-compliance-scan-results" element={
                <ProtectedRoute>
                  <ExternalIntegrationProtectedRoute requiredIntegration="azureAi">
                    <AiSupervisoryReviewProtectedRoute>
                      <AIComplianceScanResults />
                    </AiSupervisoryReviewProtectedRoute>
                  </ExternalIntegrationProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Monthly Health Review Window - For opening in new window */}
              <Route path="/monthly-health-review-window" element={<BundleProtectedRoute requiredBundle="adult-day-services"><MonthlyHealthReviewWindow /></BundleProtectedRoute>} />

              {/* Home Health (PCA) Document Windows - For opening in new window */}
              <Route path="/pca-document-reader" element={<BundleProtectedRoute requiredBundle="home-health"><PcaDocumentReader /></BundleProtectedRoute>} />
              <Route path="/pca-supervision" element={<BundleProtectedRoute requiredBundle="home-health"><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><FormClientBoundary><SupervisionWindow /></FormClientBoundary></OrgFeatureProtectedRoute></BundleProtectedRoute>} />
              <Route path="/pca-care-plan" element={<BundleProtectedRoute requiredBundle="home-health"><FormClientBoundary><CarePlanWindow /></FormClientBoundary></BundleProtectedRoute>} />
              <Route path="/pca-face-sheet" element={<BundleProtectedRoute requiredBundle="home-health"><FormClientBoundary><FaceSheetWindow /></FormClientBoundary></BundleProtectedRoute>} />
              <Route path="/pca-bill-of-rights" element={<BundleProtectedRoute requiredBundle="home-health"><FormClientBoundary><BillOfRightsWindow /></FormClientBoundary></BundleProtectedRoute>} />
              <Route path="/pca-home-care-bill-of-rights" element={<BundleProtectedRoute requiredBundle="home-health"><FormClientBoundary><HomeCareBillOfRightsWindow /></FormClientBoundary></BundleProtectedRoute>} />
              <Route path="/pca-county-roi" element={<BundleProtectedRoute requiredBundle="home-health"><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><FormClientBoundary><CountyROIWindow /></FormClientBoundary></OrgFeatureProtectedRoute></BundleProtectedRoute>} />
              <Route path="/pca-general-roi" element={<BundleProtectedRoute requiredBundle="home-health"><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><FormClientBoundary><GeneralPCAROIWindow /></FormClientBoundary></OrgFeatureProtectedRoute></BundleProtectedRoute>} />
              <Route path="/pca-discharge-summary" element={<BundleProtectedRoute requiredBundle="home-health"><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><FormClientBoundary><DischargeSummaryWindow /></FormClientBoundary></OrgFeatureProtectedRoute></BundleProtectedRoute>} />
              <Route path="/pca-participant-rep" element={<BundleProtectedRoute requiredBundle="home-health"><OrgFeatureProtectedRoute requiredFeature="wecare_template_forms"><FormClientBoundary><ParticipantRepWindow /></FormClientBoundary></OrgFeatureProtectedRoute></BundleProtectedRoute>} />

              {/* Public Client Form - No auth required (token-based access) */}
              <Route path="/client-form/:token" element={<ClientFormPage />} />
              <Route path="/client-stat-survey/:token" element={<ClientStatSurveyPage />} />
              <Route path="/rp-sign/:token" element={<RpTimesheetSign />} />
              <Route path="/roi-sign/:token" element={<RoiSign />} />
              <Route path="/new-hire/:token" element={<NewHireProfile />} />
              <Route path="/cm-sign/:token" element={<CaseManagerSign />} />
              {/* 245D legal representative signing link: date of birth first, no login */}
              <Route path="/rep-sign/:token" element={<LegalRepresentativeSign />} />
              <Route path="/pharmacy" element={<ProtectedRoute><PharmacyPortal /></ProtectedRoute>} />
              <Route path="/intake/:token" element={<IntakePacket />} />
              <Route path="/telehealth/join/:token" element={<TelehealthGuestJoin />} />
              {/* Telehealth client signature with a one-time code (no sign-in) */}
              <Route path="/sign" element={<ClientSign />} />
              <Route path="/demo" element={<DemoBooking />} />
              <Route path="/demo/cancel/:token" element={<DemoCancel />} />

              {/* Patient Portal Routes - Separate auth system for patients */}
              <Route path="/patient-portal/*" element={
                <PatientAuthProvider>
                  <Routes>
                    <Route path="login" element={<PatientLogin />} />
                    <Route path="activate" element={<PatientActivation />} />
                    <Route element={
                      <PatientProtectedRoute>
                        <PatientPortalLayout />
                      </PatientProtectedRoute>
                    }>
                      <Route index element={<Navigate to="/patient-portal/dashboard" replace />} />
                      <Route path="dashboard" element={<PatientDashboard />} />
                      <Route path="appointments" element={<PatientAppointments />} />
                      <Route path="appointments/:appointmentId" element={<PatientAppointments />} />
                      <Route path="documents" element={<PatientDocuments />} />
                      <Route path="messages/*" element={<Navigate to="/patient-portal/dashboard" replace />} />
                      <Route path="profile" element={<PatientProfile />} />
                    </Route>
                  </Routes>
                </PatientAuthProvider>
              } />
            </Routes>
            </Suspense>
            <ThemedToastContainer />

            {/* Asks "which copy?" for any window whose draft save needs a choice */}
            <DraftRecoveryPrompt />

            {/* SessionMonitor - Auto-logout when token expires */}
            <SessionMonitor />
            
            {/* ActivityBasedTokenRefresh - Proactively refresh token when user is active */}
            <ActivityBasedTokenRefresh />
            
            {/* InactivityMonitor - Auto-logout after 60 minutes of inactivity */}
            <InactivityMonitor />

            {/* SessionDebugWidget - Dev-only timer display in upper right corner */}
            <SessionDebugWidget />
          </Router>
          </NotificationsProvider>
          </AlertsProvider>
        </UserManagementProvider>
      </AccessControlProvider>
    </FeatureVisibilityProvider>
    </IntegratedSidebarProvider>
    </AuthProvider>
    </ThemeProvider>
    </LiveRegionProvider>
    {/* Keep development launchers clear of both rows of narrow form actions. */}
    <div className="arasync-query-devtools">
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </div>
    </QueryClientProvider>
    </>
  );
}

export default App;
